/**
 * AI CHAT EVENT LISTENERS
 *
 * React hook that subscribes to Tauri events from the AI chat backend.
 * Updates aiChatStore in response to streaming events.
 * Also hydrates persisted sessions on startup.
 *
 * No-op on web (non-Tauri) builds.
 */

import { useEffect } from "react";
import { isTauri } from "@/hooks/usePlatform";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useChatTabStore } from "@/stores/chatTabStore";
import type { AiEvent } from "@/types/ai-chat";

/**
 * Hydrate persisted chat sessions after stores rehydrate from localStorage.
 * For each tab with a sessionId that is NOT already in aiChatStore,
 * loads the events from disk via Tauri and replays them.
 */
async function hydrateSessions() {
  const chatTabState = useChatTabStore.getState();
  const aiChatState = useAiChatStore.getState();

  for (const pid of Object.keys(chatTabState.tabs)) {
    const projectId = Number(pid);
    const tabs = chatTabState.tabs[projectId] ?? [];

    for (const tab of tabs) {
      if (!tab.sessionId) continue;
      if (aiChatState.sessions.has(tab.sessionId)) continue;

      const ok = await aiChatState.hydrateSession(tab.sessionId);
      if (!ok) {
        // Session file doesn't exist or is empty — mark tab as stale
        chatTabState.updateStatus(tab.id, "stale");
      }
    }
  }
}

export function useAiChatEvents() {
  useEffect(() => {
    if (!isTauri()) return;

    const unlisteners: (() => void)[] = [];
    let disposed = false;

    (async () => {
      const { listen } = await import("@tauri-apps/api/event");

      const u1 = await listen<AiEvent>("ai-chat:event", (e) => {
        try {
          useAiChatStore.getState().handleEvent(e.payload);
        } catch (err) {
          // A single malformed event must never tear down the listener and
          // silently freeze the chat (the "stuck on Thinking" class of bug).
          console.error("[useAiChatEvents] handleEvent threw:", err, e.payload);
        }
      });

      if (disposed) {
        u1();
        return;
      }
      unlisteners.push(u1);
    })().catch((err) => console.error("[useAiChatEvents] listener setup failed:", err));

    // Hydrate persisted sessions after a short delay to allow
    // chatTabStore to rehydrate from localStorage first
    const hydrationTimer = setTimeout(() => {
      if (!disposed) {
        hydrateSessions();
      }
    }, 1000);

    return () => {
      disposed = true;
      clearTimeout(hydrationTimer);
      unlisteners.forEach((fn) => fn());
    };
  }, []);
}
